/**
 * ATL-1452 — ONE update attempt for a bundle lockout. THE LOCK LIVES ONLY AS LONG AS THIS ATTEMPT.
 *
 * Earlier designs held the lock until something released it (a cap, a back-off record, a kill
 * switch, a "nothing newer" answer…), and every review round found another way for a till to stay
 * stuck, because "fail open" had to be re-implemented on every path that could hold it. Here there is
 * only one path: the attempt runs, and whatever it ends with other than a restart RELEASES the lock
 * (the caller does that). The next LAUNCH re-evaluates and, if still below the floor, tries again.
 * The attempt's own timeouts bound how long a till can be held BEFORE a restart is issued — typically
 * seconds; at worst 3 × (30s check + 2min download) + 25s of retry pauses + ~7s of flush/storage/drain
 * ≈ 8 minutes. Once a restart IS issued nothing times it out (an issued reload cannot be called back;
 * releasing under it would restart a till mid-sale): it succeeds (the process is gone), fails (it
 * rejects/throws → released), or neither — then the screen tells the cashier, after 15s, to close and
 * reopen the app, and that relaunch is not locked (the 'restarted' back-off is already written).
 *
 * Pure and injectable: no React, no timers of its own beyond `sleep`, so every outcome is testable.
 */

export type BundleAttemptOutcome =
  /** The update channel has nothing newer: the running bundle is the newest this till can get. */
  | 'no-newer-update'
  /** Every try failed to check or download (network, EAS unreachable, a timeout). */
  | 'update-failed'
  /** The update downloaded, but the restart into it failed; it applies on the next launch. */
  | 'restart-failed'
  /** The restart's back-off could not be recorded, so a restart could loop: none is issued. */
  | 'no-storage'
  /** The caller no longer wants the attempt (the lock was lifted, the screen went away). */
  | 'cancelled'

export interface BundleAttemptDeps {
  platform: string
  /**
   * Native: expo-updates' check / download. Must settle (the caller bounds each with a timeout).
   */
  checkForUpdate: () => Promise<{ isAvailable: boolean }>
  fetchUpdate: () => Promise<unknown>
  /**
   * Restart into the new bundle — record the 'restarted' back-off, drain in-flight writes, restart.
   * A restart that HAPPENS never settles (the process is gone). Resolves 'restart-failed' when it
   * demonstrably did not happen, 'cancelled' when it was called off before it was issued, and
   * 'vetoed' when the back-off could not be recorded — on every platform, since restarting without it
   * could loop (web: the reloaded page finds no record and reloads again; native: expo-updates rolls a
   * crashing update back, and the relaunch below the floor locks and restarts again).
   */
  restart: () => Promise<'restart-failed' | 'cancelled' | 'vetoed'>
  /** False once the attempt is no longer wanted; checked after every await. */
  stillWanted: () => boolean
  sleep: (ms: number) => Promise<void>
  /**
   * Pauses between native tries (one more try than pauses). Generous on purpose: at a cold launch
   * expo-updates' OWN launch download may be running, and our check/download is queued behind it or
   * rejected until it finishes — after which the update is already downloaded and the next try is quick.
   */
  retryDelaysMs?: readonly number[]
  /** Called per failed try, for telemetry. */
  onTryFailed?: (stage: 'check' | 'fetch', error: unknown) => void
}

export const BUNDLE_ATTEMPT_RETRY_DELAYS_MS: readonly number[] = [5000, 20000]

export const runBundleUpdateAttempt = async (
  deps: BundleAttemptDeps
): Promise<BundleAttemptOutcome> => {
  const { platform, stillWanted, retryDelaysMs = BUNDLE_ATTEMPT_RETRY_DELAYS_MS } = deps

  if (platform === 'web') {
    // A web build has no OTA: the "update" is a reload onto the hosted build. The restart records a
    // 'restarted' back-off first, so a reloaded page that is STILL below the floor (the hosted build is
    // the newest there is) is not locked again at its launch — never a reload loop.
    if (!stillWanted()) return 'cancelled'
    const r = await deps.restart()
    return r === 'vetoed' ? 'no-storage' : r
  }

  const tries = retryDelaysMs.length + 1
  for (let attempt = 1; attempt <= tries; attempt++) {
    if (!stillWanted()) return 'cancelled'
    let available: boolean
    try {
      available = (await deps.checkForUpdate()).isAvailable
    } catch (error) {
      deps.onTryFailed?.('check', error)
      if (attempt === tries) return stillWanted() ? 'update-failed' : 'cancelled'
      await deps.sleep(retryDelaysMs[attempt - 1])
      continue
    }
    if (!stillWanted()) return 'cancelled'
    if (!available) return 'no-newer-update'
    try {
      await deps.fetchUpdate()
    } catch (error) {
      deps.onTryFailed?.('fetch', error)
      if (attempt === tries) return stillWanted() ? 'update-failed' : 'cancelled'
      await deps.sleep(retryDelaysMs[attempt - 1])
      continue
    }
    if (!stillWanted()) return 'cancelled'
    const r = await deps.restart()
    // Vetoed: nothing restarted. The update IS downloaded, so it applies at the next cold launch.
    return r === 'vetoed' ? 'no-storage' : r
  }
  return 'update-failed'
}
